/** Floor-plan-aware table join rules (adjacency + optional explicit links). */
export const JOIN_DEFAULT_TABLE_W = 100;
export const JOIN_DEFAULT_TABLE_H = 80;
/** Max gap (normalized 0–1000 canvas) between table edges to count as joinable. */
export const JOIN_ADJACENT_GAP_MAX = 48;
function tableRect(t) {
    const w = t.layout?.w && t.layout.w > 0 ? t.layout.w : JOIN_DEFAULT_TABLE_W;
    const h = t.layout?.h && t.layout.h > 0 ? t.layout.h : JOIN_DEFAULT_TABLE_H;
    return {
        x: t.layout?.x ?? 0,
        y: t.layout?.y ?? 0,
        w,
        h,
    };
}
/** Minimum gap between two axis-aligned rectangles (0 if overlapping). */
export function rectGap(a, b) {
    const dx = Math.max(0, Math.max(a.x - (b.x + b.w), b.x - (a.x + a.w)));
    const dy = Math.max(0, Math.max(a.y - (b.y + b.h), b.y - (a.y + a.h)));
    if (dx === 0 && dy === 0)
        return 0;
    if (dx === 0)
        return dy;
    if (dy === 0)
        return dx;
    return Math.hypot(dx, dy);
}
export function hasFloorPlanLayouts(tables) {
    return tables.some((t) => t.layout &&
        typeof t.layout.x === "number" &&
        typeof t.layout.y === "number");
}
function explicitlyLinked(a, b) {
    return ((a.joinable_with || []).includes(b._id) ||
        (b.joinable_with || []).includes(a._id));
}
/**
 * Two tables can be combined for one booking only when the venue has said so: either an
 * explicit `joinable_with` link, or (when a floor plan exists) the tables sit next to each
 * other. Tables with no layout and no links are never joinable, so the guest-facing party
 * cap equals the largest single table unless combinations are configured.
 */
export function tablesAreJoinable(a, b, useLayoutRules) {
    if (a._id === b._id)
        return false;
    if (explicitlyLinked(a, b))
        return true;
    if (!useLayoutRules)
        return false;
    if (!a.layout || !b.layout)
        return false;
    const gap = rectGap(tableRect(a), tableRect(b));
    return gap <= JOIN_ADJACENT_GAP_MAX;
}
/** Join set must be connected under joinability (for multi-table combos). */
export function isConnectedJoinSet(tableIds, tables) {
    if (tableIds.length <= 1)
        return true;
    const byId = new Map(tables.map((t) => [t._id, t]));
    const useLayout = hasFloorPlanLayouts(tables);
    const ids = tableIds.filter((id) => byId.has(id));
    if (ids.length !== tableIds.length)
        return false;
    const canJoin = (x, y) => {
        const a = byId.get(x);
        const b = byId.get(y);
        return tablesAreJoinable(a, b, useLayout);
    };
    const visited = new Set([ids[0]]);
    const queue = [ids[0]];
    while (queue.length) {
        const cur = queue.shift();
        for (const other of ids) {
            if (visited.has(other))
                continue;
            if (!canJoin(cur, other))
                continue;
            visited.add(other);
            queue.push(other);
        }
    }
    return visited.size === ids.length;
}
export function isValidJoinCombo(tableIds, tables) {
    if (!tableIds.length)
        return false;
    if (tableIds.length === 1)
        return true;
    return isConnectedJoinSet(tableIds, tables);
}
//# sourceMappingURL=table_join.js.map